import { NextPage } from "next"
import Error from "next/error"
import Head from "next/head"
import React from "react"
import styled, { css } from "styled-components"

import AutoLink from "~/components/atoms/AutoLink"
import TransitionLink from "~/components/atoms/TransitionLink"
import Typography from "~/components/atoms/Typography"
import { Container } from "~/components/layouts/Container"
import Breadcrumb from "~/components/organisms/Breadcrumb"
import DetailMenu from "~/components/organisms/DetailMenu"
import Footer from "~/components/organisms/Footer"
import Header from "~/components/organisms/Header"
import RecruitmentHead from "~/components/organisms/RecruitmentHead"
import SchduleCard from "~/components/organisms/SchduleCard"
import Table from "~/components/organisms/Table"
import { IsPC, IsMobile, SpacingSize, Color, TextSize, Z, LineHeight } from "~/consts/style-mixins"
import ObservedActionBar from "~/observers/ActionBar"
import { internalLink } from "~/services/helper"
import { InfraNextPageContext } from "~/types/InfraNextPageContext"
import { ReturnGetInitialProps } from "~/types/ReturnGetInitialProps"

const getInitialProps = async ({ initialStoreData: { shortInternshipStore }, query, res }: InfraNextPageContext) => {
  const shortInternship = await shortInternshipStore.fetchById(Number(query["short-internship-id"]))
  const sortedSchedules = shortInternship?.sortedSchedules
  if (!shortInternship) {
    res.statusCode = 404
  }

  return { shortInternship, sortedSchedules }
}

type GetInitialProps = ReturnGetInitialProps<typeof getInitialProps>

const Page: NextPage<GetInitialProps> = ({ shortInternship, sortedSchedules }) => {
  if (!shortInternship) {
    return <Error statusCode={404} />
  }

  const crumbs = [
    {
      name: "短期インターンシップ",
      link: internalLink(`/short-internships`),
    },
    {
      name: shortInternship.title,
      link: internalLink(`/short-internships/${shortInternship.id}`),
    },
  ]

  const targetGradYears = () => {
    if (shortInternship.targetGradYears.length !== 0) {
      const targetGradYears = shortInternship.targetGradYears.join(`年卒 / `)
      return `${targetGradYears}年卒`
    } else {
      return `全学年対象`
    }
  }

  return (
    <>
      <Head>
        <title>{shortInternship.title} | Infra（インフラ）</title>
        <meta
          name="description"
          content={`Infra（インフラ）では${shortInternship.company.name}の${shortInternship.jobType}向けの短期インターンシップ募集情報を掲載しています。`}
        />
        <meta name="og:title" content={`${shortInternship.title} | Infra（インフラ）`} />
        <meta name="og:description" content={`${shortInternship.content.replace(/\r?\n/g, "").slice(0, 130)}…`} />
        <meta name="og:image" content={shortInternship.thumbnailImgUrl} />
      </Head>
      <Header showTab logoType="ShortIntern" />
      <Container size="Large">
        <StyledInnerContainer>
          <StyledDetailMenuWrapper>
            <DetailMenu company={shortInternship.company} selectedTabIndex={7} />
          </StyledDetailMenuWrapper>
          <StyledMainContentWrapper>
            <StyledMainContentInner>
              <StyledRecruitmentHead category="shortInternship" recruitment={shortInternship} />
              <div>
                <StyledEachItemWrapper>
                  <StyledAutoLink>{shortInternship.content}</StyledAutoLink>
                </StyledEachItemWrapper>
                <StyledEachItemWrapper>
                  <StyledHeading type="SubHero">日程</StyledHeading>
                  {sortedSchedules?.map((schedule, index) => {
                    return (
                      <StyledShortInternshipSchduleCardWrapper key={index}>
                        <SchduleCard
                          key={index}
                          schedule={schedule}
                          externalApplication={shortInternship.isExternalApplication}
                          applicationUrl={shortInternship.applicationUrl}
                        />
                      </StyledShortInternshipSchduleCardWrapper>
                    )
                  })}
                </StyledEachItemWrapper>
                <StyledEachItemWrapper>
                  <StyledHeading type="SubHero">職種</StyledHeading>
                  <StyledJobtype>{shortInternship.jobType}</StyledJobtype>
                </StyledEachItemWrapper>
                <StyledEachItemWrapper>
                  <StyledHeading type="SubHero">応募条件/待遇</StyledHeading>
                  <StyledTableWrapper>
                    <Table
                      items={[
                        {
                          name: "対象学年",
                          content: targetGradYears(),
                        },
                        {
                          name: "応募資格",
                          content: shortInternship.qualification,
                        },
                        {
                          name: "採用人数",
                          content: shortInternship.expectedRecruitmentNumbers,
                        },
                        {
                          name: "報酬",
                          content: shortInternship.reward,
                        },
                        {
                          name: "選考フロー",
                          content: shortInternship.selectionFlow,
                        },
                      ]}
                    />
                  </StyledTableWrapper>
                </StyledEachItemWrapper>
              </div>
              <StyledTransitionLink
                href={internalLink(`/companies/${shortInternship.company.id}/recruitments`)}
                size="Large"
                isButton
                icon="Back"
              >
                この会社の他の募集を見る
              </StyledTransitionLink>
            </StyledMainContentInner>
          </StyledMainContentWrapper>
        </StyledInnerContainer>
      </Container>
      <Breadcrumb crumbs={crumbs} />
      <ActionBarWrapper>
        <ObservedActionBar
          type="shortInternship"
          id={shortInternship.id}
          status={shortInternship.status}
          applicationUrl={shortInternship.isExternalApplication ? shortInternship.applicationUrl : undefined}
          schedules={shortInternship.schedules}
        />
      </ActionBarWrapper>
      <Footer />
    </>
  )
}

Page.getInitialProps = getInitialProps

export default Page

const StyledInnerContainer = styled.div`
  ${IsPC(css`
    padding: 48px 0;
    display: flex;
    flex-wrap: wrap;
  `)}
`

const StyledMainContentWrapper = styled.div`
  width: 100%;
  ${IsPC(css`
    width: calc(100% - 280px);
  `)}
  ${IsMobile(css`
    padding: 24px 0 32px;
  `)}
`

const StyledMainContentInner = styled.div`
  ${IsPC(css`
    padding-left: 48px;
  `)}
`

const StyledDetailMenuWrapper = styled.div`
  position: sticky;
  z-index: ${Z.Tab};
  ${IsPC(css`
    top: 16px;
    height: 100%;
  `)}
  ${IsMobile(css`
    top: 0;
  `)}
`

const StyledRecruitmentHead = styled(RecruitmentHead)`
  margin-bottom: ${SpacingSize.XL}px;
`

const StyledEachItemWrapper = styled.div`
  margin-bottom: ${SpacingSize["2XL"]}px;
`

const StyledAutoLink = styled(AutoLink)`
  font-size: ${TextSize.M}px;
  line-height: ${LineHeight.L}%;
  white-space: pre-line;
  word-break: break-all;
`

const StyledTransitionLink = styled(TransitionLink)`
  max-width: 280px;
  width: 100%;
  margin: ${SpacingSize["2XL"]}px auto 0;
  display: block;
`

const StyledHeading = styled(Typography)`
  margin-bottom: ${SpacingSize.XS}px;
  color: ${Color.Primary};
`

const StyledShortInternshipSchduleCardWrapper = styled.div`
  margin-top: 12px;
`

const StyledTableWrapper = styled.div`
  margin-top: 16px;
`

const StyledJobtype = styled.div`
  margin-top: 12px;
  font-size: ${TextSize.M}px;
  font-weight: bold;
`

const ActionBarWrapper = styled.div`
  position: fixed;
  bottom: 16px;
  left: calc(50% + 164px - 200px);
  width: 100%;
  max-width: 400px;
  z-index: ${Z.Fixed};
  ${IsMobile(css`
    left: 0;
    max-width: inherit;
  `)}
`
